import Head from 'next/head'
import Script from 'next/script'
import { FaShieldAlt, FaBroom, FaTools, FaSnowflake, FaCheckCircle } from 'react-icons/fa'
import { GiMagicBroom } from 'react-icons/gi'
import { MdAir } from 'react-icons/md'
import { LegalPageLayout, LegalHighlight } from '../../components/index/LegalPageLayout'

export default function WarrantyClaims() {
  return (
    <>
      <Head>
        <title>Warranty Claims By Service Requester | StringsSG</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"/>
        <meta name="description" content="These terms apply to service providers when they cancel the service request or the consumer client cancels the service request."/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:url" content="https://www.stringssg.com/terms/warranty-claims" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Warranty Claims By Service Requester | StringsSG"/>
        <meta property="og:description" content="These terms apply to service providers when they cancel the service request or the consumer client cancels the service request." />
        <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/Google-icon.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/Google-icon.png" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/terms/warranty-claims"/>
        <meta name="twitter:title" content="Warranty Claims By Service Requester | StringsSG"/> 
        <meta name="twitter:description" content="These terms apply to service providers when they cancel the service request or the consumer client cancels the service request."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/terms/warranty-claims"/>
      </Head>
      
      <LegalPageLayout
        title="Warranty Claims"
        subtitle="Warranty Claims by Consumer Clients to Service Providers"
        icon={FaShieldAlt}
        iconBgColor="from-teal-500 to-emerald-600"
        heroBgColor="from-slate-900 via-teal-900 to-emerald-900"
      >
        <LegalHighlight color="green">
          <div className="flex items-start gap-3">
            <FaShieldAlt className="text-teal-600 text-xl mt-1 shrink-0" />
            <p>
              Our warranty terms ensure quality assurance for all services provided through the StringsSG platform. Please review the specific warranty periods for each service type below.
            </p>
          </div>
        </LegalHighlight>

        {/* Part-Time Helper */}
        <div id="part-time-helper" className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <GiMagicBroom className="text-2xl text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">1. Part-Time Helper Cleaner Service</h2>
              <span className="text-sm text-slate-500">Domestic Services Segment</span>
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold text-slate-500 uppercase">Warranty Period</span>
            </div>
            <p className="text-lg font-bold text-slate-700">No warranty period provided</p>
          </div>
          <p className="text-slate-600">
            For services in our domestic services segment, there is no warranty period provided. However, we encourage consumer clients to report any issues they 
            encounter with the service. In addition, consumer clients have the option to express their feedback through a rating and review on the service 
            provider&apos;s profile. Your feedback helps us maintain the highest quality of service.
          </p>
        </div>

        {/* Handyman Services */}
        <div id="handyman-service" className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <FaTools className="text-2xl text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">2. Handyman Services</h2>
              <span className="text-sm text-slate-500">General Repairs & Maintenance</span>
            </div>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <FaCheckCircle className="text-amber-600" />
              <span className="text-sm font-bold text-amber-800 uppercase">Workmanship Warranty</span>
            </div>
            <p className="text-2xl font-bold text-amber-700">30 Days</p>
          </div>
          
          <div className="text-slate-600 space-y-4">
            <p>
              For handyman services, the service provider must agree to a 30-day workmanship warranty for all services rendered. This means that if any issues 
              arise within 30 days after the service is provided, if related to or caused by the last service, the service provider will address and rectify those issues at no additional cost to the consumer client.
            </p>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="font-semibold text-slate-700 mb-2">Part Replacement & Installation</p>
              <p className="text-sm">
                If the service includes part replacement or new system installation, please note that the manufacturer&apos;s warranty will apply to the installed items. 
                The manufacturer&apos;s warranty typically covers defects or issues related to the specific parts or systems installed, and the consumer client should refer to the manufacturer&apos;s terms for details.
              </p>
            </div>
          </div>
        </div>

        {/* Aircon Services */}
        <div id="aircon-service" className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
              <MdAir className="text-2xl text-sky-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">3. Aircon Services</h2>
              <span className="text-sm text-slate-500">Air Conditioning Maintenance & Repair</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 text-center">
              <p className="text-xs font-bold text-sky-800 uppercase mb-1">Standard Service</p>
              <p className="text-2xl font-bold text-sky-700">30 Days</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-xs font-bold text-blue-800 uppercase mb-1">Chemical Services</p>
              <p className="text-2xl font-bold text-blue-700">60 Days</p>
              <p className="text-xs text-blue-600 mt-1">Wash, Overhaul, Drainage</p>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-center">
              <p className="text-xs font-bold text-indigo-800 uppercase mb-1">Gas Refill</p>
              <p className="text-2xl font-bold text-indigo-700">90 Days</p>
              <p className="text-xs text-indigo-600 mt-1">Refrigerant Gas</p>
            </div>
          </div>
          
          <div className="text-slate-600 space-y-4">
            <p>
              For aircon services, the service provider must agree to a 30-day workmanship warranty for all services rendered. This warranty ensures that any issues 
              arising within 30 days after the service, if related to or caused by the last service will be addressed and resolved by the service provider, with no 
              additional charges to the consumer client.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <FaCheckCircle className="text-green-500 shrink-0" />
                <span><strong>Chemical wash, chemical overhaul, or chemical drainage flushing:</strong> 60-day warranty</span>
              </div>
              <div className="flex items-center gap-2">
                <FaCheckCircle className="text-green-500 shrink-0" />
                <span><strong>Refrigerant gas refill:</strong> 90-day warranty</span>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="font-semibold text-slate-700 mb-2">Part Replacement & Installation</p>
              <p className="text-sm">
                If the service includes part replacement or new system installation, please note that the manufacturer&apos;s warranty will apply to the installed items. 
                The manufacturer&apos;s warranty typically covers defects or issues related to the specific parts or systems installed, and the consumer client should refer to the manufacturer&apos;s terms for details.
              </p>
            </div>
          </div>
        </div>
      </LegalPageLayout>

      <Script 
        id="google-tagmanager-new"
        src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
        strategy="lazyOnload"
        onLoad={() => {              
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-QD1HGGHDYJ');
        }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
        {`!function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '128634041116395');
          fbq('track', 'PageView');`}
      </Script>
    </>
  )
}
